import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { coursesApi } from '@/api/courses';
import { usersApi } from '@/api/users';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { MPickerChip } from '@/components/mobile/MPickerChip';
import { MTitle } from '@/components/mobile/MobileKit';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GroupsPanel } from './groups/GroupsPanel';

/**
 * Группы — отдельный раздел админки.
 *
 * ⚠️ ЗАЧЕМ ОТДЕЛЬНЫЙ РАЗДЕЛ, ЕСЛИ ПАНЕЛЬ УЖЕ ЕСТЬ НА СТРАНИЦЕ КУРСА.
 * Потому что найти её там нельзя: она лежит внутри вкладки конкретного курса, и
 * администратор, которому нужно «раскидать поток по группам», сначала должен
 * догадаться зайти в курс. 04.09.2026 заказчик так и не нашёл групп вовсе.
 *
 * Сама панель — ОДНА на оба экрана (`groups/GroupsPanel`): копия разошлась бы с
 * оригиналом на первой же правке.
 */
export function AdminGroupsPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [marathonId, setMarathonId] = useState('');

  const marathons = useQuery({
    queryKey: ['admin', 'marathons', 'for-groups'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 100 }),
  });

  // Кураторы нужны панели, чтобы их можно было прикрепить к группе.
  const curators = useQuery({
    queryKey: ['admin', 'curators'],
    queryFn: () => usersApi.list({ role: 'curator', limit: 200 }),
  });
  const curatorList = curators.data?.items ?? [];

  const courses = marathons.data?.items ?? [];

  return (
    <div className="space-y-6 max-md:space-y-3">
      {isMobile ? (
        // Телефон (§5.9): шапка набора и курс — фишкой с окном выбора и поиском; курсов на платформе под сотню, и
        // в выпадающем списке на 390 их названия обрезались.
        <>
          <MTitle title={t('adminGroups.pageTitle')} subtitle={t('adminGroups.pageSubtitle')} />
          <div data-groups-course-picker>
            <MPickerChip
              label={t('adminGroups.pickCoursePlaceholder')}
              value={marathonId}
              options={[
                { value: '', label: t('adminGroups.pickCoursePlaceholder') },
                ...courses.map((m) => ({ value: m.id, label: m.title })),
              ]}
              onChange={setMarathonId}
            />
          </div>
          <p className="px-0.5 text-[12px] text-on-surface-variant">{t('adminGroups.pageHint')}</p>
        </>
      ) : (
      <>
      <PageHeader title={t('adminGroups.pageTitle')} subtitle={t('adminGroups.pageSubtitle')} />

      <Card>
        <label className="block text-sm">
          <span className="mb-1 block font-semibold text-on-surface">{t('adminGroups.pickCourse')}</span>
          <select
            value={marathonId}
            onChange={(e) => setMarathonId(e.target.value)}
            className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
          >
            <option value="">{marathons.isLoading ? '…' : t('adminGroups.pickCoursePlaceholder')}</option>
            {courses.map((m) => (
              <option key={m.id} value={m.id}>
                {m.title}
              </option>
            ))}
          </select>
        </label>
        <p className="mt-2 text-xs text-on-surface-variant">{t('adminGroups.pageHint')}</p>
      </Card>
      </>
      )}

      {/* Панель показывается только после выбора курса: группа принадлежит
          курсу, и «все группы разом» без курса не имеют смысла. */}
      {marathonId && (
        <GroupsPanel
          marathonId={marathonId}
          curatorList={curatorList}
          selectedIds={[]}
          onAssigned={() => {}}
        />
      )}
    </div>
  );
}
